import type { CSSProperties, FC, ReactNode } from 'react'

import {
    CircleCheck,
    CircleDashed,
    FileText,
    LoaderCircle,
    Network,
    ShieldCheck,
    Timer
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { MiniBars } from '@/components/charts/mini-bars'
import { DeltaBadge } from '@/components/charts/delta-badge'
import { ProgressBar } from '@/components/charts/progress-bar'
import { FloatingCard, PreviewFrame } from '@/components/features/preview-frame'
import { CHART_COLORS } from '@/constants/dashboard'

const BURN = [182000, 176000, 168000, 161000, 155000, 149000]

export const StartupRunway: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>Runway and burn</p>
                        <p className='text-[11px] text-stone'>Updated this morning from 4 accounts</p>
                    </div>

                    <span className='rounded-full bg-gold/25 px-2.5 py-1 text-[11px] font-medium text-gold-dark'>Seed stage</span>
                </div>

                <div className='mt-6 grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end'>
                    <div>
                        <p className='text-[11px] text-stone-dark'>Runway at current burn</p>

                        <p className='mt-1 flex items-baseline gap-2 font-heading text-[2.6rem] font-medium leading-none tracking-[-0.04em]'>
                            26
                            <span className='font-sans text-base font-normal tracking-normal text-stone-dark'>months</span>
                        </p>

                        <div className='mt-3'>
                            <DeltaBadge value={18.2} suffix='vs last quarter' />
                        </div>
                    </div>

                    <div className='w-full sm:w-44'>
                        <p className='mb-2 text-[11px] text-stone-dark'>Net burn, last 6 months</p>
                        <MiniBars values={BURN} color={CHART_COLORS.terracotta} height={64} />
                    </div>
                </div>

                <dl className='mt-6 grid grid-cols-3 gap-2 border-t border-burgundy/[0.06] pt-5'>
                    {[
                        { label: 'Cash in bank', value: '$3.9M' },
                        { label: 'Monthly burn', value: '$149K' },
                        { label: 'Zero cash date', value: 'Dec 2028' }
                    ].map(item => (
                        <div key={item.label} className='rounded-xl bg-ivory p-3'>
                            <dt className='text-[10px] text-stone-dark'>{item.label}</dt>
                            <dd className='mt-1 text-sm font-medium tabular-nums'>{item.value}</dd>
                        </div>
                    ))}
                </dl>
            </PreviewFrame>

            <FloatingCard className='-bottom-8 -right-4 w-56 lg:-right-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <FileText aria-hidden='true' className='size-4 text-gold-dark' />
                    Investor update drafted
                </p>

                <p className='mt-1 text-[11px] text-stone'>Metrics, runway and commentary for Q3</p>
            </FloatingCard>
        </div>
    )
}

const CLOSE_TASKS = [
    { task: 'Bank and card reconciliations', owner: 'Automated', status: 'done' },
    { task: 'Accruals and prepaids', owner: 'Controller', status: 'done' },
    { task: 'Payroll journal entries', owner: 'Automated', status: 'done' },
    { task: 'Revenue recognition review', owner: 'Revenue lead', status: 'review' },
    { task: 'Flux analysis and commentary', owner: 'Drafted by AI', status: 'waiting' },
    { task: 'Management pack', owner: 'Scheduled for day 5', status: 'waiting' }
] as const

const STATUS_ICON = {
    done: { Icon: CircleCheck, className: 'text-sage-dark', label: 'Done' },
    review: { Icon: LoaderCircle, className: 'text-gold-dark', label: 'In review' },
    waiting: { Icon: CircleDashed, className: 'text-stone', label: 'Up next' }
}

export const CloseChecklist: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>September close</p>
                        <p className='text-[11px] text-stone'>4 of 6 steps complete</p>
                    </div>

                    <span className='rounded-full bg-burgundy px-2.5 py-1 text-[11px] font-medium text-cream'>Day 3 of 5</span>
                </div>

                <ProgressBar value={0.68} size='md' className='mt-5' label='Close progress' />

                <ul className='mt-5 space-y-1.5' data-chart=''>
                    {CLOSE_TASKS.map((item, index) => {
                        const status = STATUS_ICON[item.status]

                        return (
                            <li
                                key={item.task}
                                className='chart-fade flex items-center gap-3 rounded-xl bg-ivory px-3 py-2.5 text-xs'
                                style={{ '--chart-delay': `${index * 120}ms` } as CSSProperties}
                            >
                                <status.Icon aria-hidden='true' className={cn('size-4 shrink-0', status.className)} />

                                <span className='min-w-0 flex-1'>
                                    <span className={cn('block truncate font-medium', item.status === 'done' && 'text-stone-dark line-through decoration-burgundy/20')}>{item.task}</span>
                                    <span className='block truncate text-[11px] text-stone'>{item.owner}</span>
                                </span>

                                <span className='hidden text-[10px] font-medium text-stone-dark sm:inline'>{status.label}</span>
                            </li>
                        )
                    })}
                </ul>
            </PreviewFrame>

            <FloatingCard slow className='-right-4 -top-8 w-52 lg:-right-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <Timer aria-hidden='true' className='size-4 text-gold-dark' />
                    Close time
                </p>

                <p className='mt-1 font-heading text-xl font-medium tracking-tight'>12 days to 5</p>
            </FloatingCard>
        </div>
    )
}

const ENTITIES = [
    { name: 'Arden Inc.', region: 'United States', currency: 'USD', revenue: '$2.84M', status: 'Closed' },
    { name: 'Arden Ltd.', region: 'United Kingdom', currency: 'GBP', revenue: '$1.12M', status: 'Closed' },
    { name: 'Arden GmbH', region: 'Germany', currency: 'EUR', revenue: '$0.86M', status: 'In review' },
    { name: 'Arden K.K.', region: 'Japan', currency: 'JPY', revenue: '$0.41M', status: 'In progress' }
]

export const EntityConsolidation: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>Consolidated revenue</p>
                        <p className='text-[11px] text-stone'>Q3 2026, reported in USD</p>
                    </div>

                    <span className='inline-flex items-center gap-1.5 rounded-full bg-burgundy/[0.06] px-2.5 py-1 text-[11px] font-medium text-stone-dark'>
                        <Network aria-hidden='true' className='size-3.5' />
                        4 entities
                    </span>
                </div>

                <table className='mt-5 w-full text-left text-xs'>
                    <caption className='sr-only'>Revenue by entity, consolidated in US dollars</caption>

                    <thead>
                        <tr className='text-[10px] uppercase tracking-[0.12em] text-stone'>
                            <th scope='col' className='pb-2 font-mono font-normal'>Entity</th>
                            <th scope='col' className='hidden pb-2 font-mono font-normal sm:table-cell'>Currency</th>
                            <th scope='col' className='pb-2 text-right font-mono font-normal'>Revenue</th>
                            <th scope='col' className='pb-2 text-right font-mono font-normal'>Status</th>
                        </tr>
                    </thead>

                    <tbody>
                        {ENTITIES.map(entity => (
                            <tr key={entity.name} className='border-t border-burgundy/[0.06]'>
                                <td className='py-2.5'>
                                    <span className='block font-medium'>{entity.name}</span>
                                    <span className='block text-[11px] text-stone'>{entity.region}</span>
                                </td>
                                <td className='hidden py-2.5 text-stone-dark sm:table-cell'>{entity.currency}</td>
                                <td className='py-2.5 text-right font-medium tabular-nums'>{entity.revenue}</td>
                                <td className='py-2.5 text-right'>
                                    <span className={cn(
                                        'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium',
                                        entity.status === 'Closed' ? 'bg-sage/20 text-sage-dark' : 'bg-gold/20 text-gold-dark'
                                    )}>
                                        {entity.status}
                                    </span>
                                </td>
                            </tr>
                        ))}

                        <tr className='border-t border-burgundy/[0.06] text-stone-dark'>
                            <td className='py-2.5'>Intercompany eliminations</td>
                            <td className='hidden sm:table-cell' />
                            <td className='py-2.5 text-right tabular-nums'>&minus;$0.18M</td>
                            <td />
                        </tr>

                        <tr className='border-t border-burgundy/15'>
                            <td className='py-3 font-medium'>Consolidated</td>
                            <td className='hidden sm:table-cell' />
                            <td className='py-3 text-right font-heading text-base font-medium tabular-nums'>$5.05M</td>
                            <td />
                        </tr>
                    </tbody>
                </table>

                <div className='mt-4 flex flex-wrap gap-2 border-t border-burgundy/[0.06] pt-4'>
                    {['SSO enforced', 'Two-step approvals', 'Audit log on'].map(item => (
                        <span key={item} className='inline-flex items-center gap-1.5 rounded-full bg-ivory px-2.5 py-1 text-[11px] text-stone-dark'>
                            <ShieldCheck aria-hidden='true' className='size-3.5 text-sage-dark' />
                            {item}
                        </span>
                    ))}
                </div>
            </PreviewFrame>

            <FloatingCard className='-bottom-8 -left-4 w-60 lg:-left-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <CircleCheck aria-hidden='true' className='size-4 text-sage-dark' />
                    Eliminations matched
                </p>

                <p className='mt-1 text-[11px] text-stone'>14 intercompany transactions paired automatically</p>
            </FloatingCard>
        </div>
    )
}
